import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout, Skeleton } from "@/components/ui/feedback";
import { useSkills } from "@/lib/queries";
import {
  DEFAULT_LINE_LEVEL, MIN_LEVEL, assignableSkills, lineSkillLevel, refusedLineSkills,
  setLineSkill, unheldRequirements, withoutRefused,
} from "@/lib/skills";
import type { RoutingSkill, SkillRequirement } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The skills a line asks of the reps it offers calls to (ADR-015): each of the tenant's rep skills
 * is required or it is not, and a rep must hold every one ticked here to be offered the line's
 * calls. What was stored and the server now refuses is named with a way to remove it, since a save
 * that sends it fails whole. Until the catalog loads the list is left as it is, so a slow load
 * never wipes it.
 */
export function LineSkillsField({
  value, onChange, tenantId, enabled, relaxSeconds, children,
}: {
  value: SkillRequirement[];
  onChange: (next: SkillRequirement[]) => void;
  tenantId: number;
  enabled: boolean;
  /** The line's relax time, for the note on a required skill nobody holds. */
  relaxSeconds: number;
  /** Settings that read with the list (the relax time), shown under it. */
  children?: React.ReactNode;
}) {
  const catalog = useSkills({ enabled });
  const assignable = catalog.data ? assignableSkills(catalog.data, tenantId) : [];
  const refused = catalog.data ? refusedLineSkills(value, catalog.data, tenantId) : [];
  const unheld = unheldRequirements(value, assignable);

  return (
    <section className="space-y-2" aria-labelledby="line-skills-label">
      <div>
        <p id="line-skills-label" className="text-xs font-medium text-text">
          Skills
        </p>
        <p className="text-xs text-text-muted">
          Only reps who hold every skill ticked here are offered this line's calls.
        </p>
      </div>

      {catalog.isPending ? (
        <Skeleton className="h-24" />
      ) : !catalog.data ? (
        <Callout tone="danger">
          Could not load the skills. Saving keeps this line's skills as they are.
        </Callout>
      ) : assignable.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
          The catalog has no rep skills yet.{" "}
          <Link to="/skills" className="text-accent-11 hover:underline">
            Add one on Skills.
          </Link>
        </p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
          {assignable.map((skill) => (
            <SkillChoice
              key={skill.id}
              skill={skill}
              level={lineSkillLevel(value, skill.slug)}
              onChange={(level) => onChange(setLineSkill(value, skill.slug, level, assignable))}
            />
          ))}
        </ul>
      )}

      {children}

      {unheld.length > 0 && (
        <Callout tone="warning" className="animate-in fade-in-0 duration-200">
          No rep holds {unheld.map((s) => s.name).join(" or ")} yet, so this line's calls{" "}
          {relaxSeconds > 0 ? "wait out the relax time before any rep may answer" : "wait for a rep who never comes"}.
          Give it to reps on the Users page.
        </Callout>
      )}
      {refused.length > 0 && (
        <Callout tone="warning">
          <p>
            This line asks for {refused.length === 1 ? "a skill" : `${refused.length} skills`} the
            catalog no longer offers, and its skills cannot be saved until{" "}
            {refused.length === 1 ? "it is" : "they are"} removed.
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {refused.map((s) => (
              <li key={s.slug}>
                {s.skill?.name ?? s.slug} <span className="font-mono">({s.slug})</span>:{" "}
                {s.problem?.toLowerCase()}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => onChange(withoutRefused(value, assignable))}
          >
            Remove {refused.length === 1 ? "it" : "them"}
          </Button>
        </Callout>
      )}
    </section>
  );
}

/** One skill the line can require, as a number's tags are ticked: held at all, or not asked for.
 *  Exported for the render test that pins the read-only cutoff badge and its survival of a
 *  re-tick, which is the half of ADR-015's console contract a helper test cannot reach. */
export function SkillChoice({
  skill, level, onChange,
}: {
  skill: RoutingSkill;
  /** Null while the line does not require it. */
  level: number | null;
  onChange: (level: number | null) => void;
}) {
  const id = `line-skill-${skill.id}`;
  const required = level !== null;
  const unheld = required && skill.holders === 0;
  // A cutoff above 1 can only have come from the API, and this form writes nothing but level 1 — so
  // it is the one thing here that cannot be typed back in once it is gone. Remembered while the
  // dialog is open and restored on a re-tick, the way HoldMedia keeps the file a user clicked away
  // from. Without it the whole row is a label, so two clicks on a skill's name quietly rewrite
  // `closer 3+` to `closer 1` and widen who may answer — and the badge's own "Saving the line keeps
  // it" would be a lie.
  const apiLevel = React.useRef<number | null>(null);
  if (level !== null && level > MIN_LEVEL) apiLevel.current = level;
  return (
    <li className="flex items-center gap-3 px-3 py-2.5 transition-colors duration-200 has-[:checked]:bg-accent-2">
      <input
        id={id}
        type="checkbox"
        className="size-3.5 shrink-0 accent-accent-9"
        checked={required}
        onChange={(e) =>
          onChange(e.target.checked ? (apiLevel.current ?? DEFAULT_LINE_LEVEL) : null)
        }
      />
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="text-sm font-medium text-text">{skill.name}</span>
          <span className="font-mono text-xs text-text-muted">{skill.slug}</span>
          {skill.category && (
            <Badge tone="outline" size="sm">
              {skill.category}
            </Badge>
          )}
        </span>
        <span
          className={cn(
            "block text-xs transition-colors duration-200",
            unheld ? "text-warning-11" : "text-text-muted",
          )}
        >
          {unheld
            ? "No rep holds it yet"
            : skill.holders === 1 ? "1 rep holds it" : `${skill.holders} reps hold it`}
        </span>
      </label>
      {/* A minimum above 1 came from the API and is kept on save rather than edited: a cutoff
       *  decides who may answer at all, which is not what this form is for. */}
      {level !== null && level > MIN_LEVEL && (
        <span
          className="shrink-0 text-xs text-text-muted"
          title="Set outside this form. Saving the line keeps it."
        >
          Level {level}+
        </span>
      )}
    </li>
  );
}
